import React, { useState } from 'react'
import { Technology } from '@/types'
import { SlideIn } from '@/components/animations/SlideIn'
import { FadeIn } from '@/components/animations/FadeIn'
import { urlFor } from '@/lib/sanity'
import Image from 'next/image'

interface SkillsVisualizationProps {
  technologies: Technology[]
}

const SkillsVisualization: React.FC<SkillsVisualizationProps> = ({ technologies }) => {
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [viewMode, setViewMode] = useState<'grid' | 'chart'>('grid')

  // Group technologies by category
  const categorizedTechnologies = technologies.reduce(
    (acc, tech) => {
      if (!acc[tech.category]) {
        acc[tech.category] = []
      }
      acc[tech.category].push(tech)
      return acc
    },
    {} as Record<string, Technology[]>
  )

  // Get unique categories
  const categories = ['all', ...Object.keys(categorizedTechnologies)]

  // Filter technologies based on selected category
  const filteredTechnologies =
    selectedCategory === 'all' ? technologies : categorizedTechnologies[selectedCategory] || []

  // Sort technologies by proficiency level (descending)
  const sortedTechnologies = [...filteredTechnologies].sort(
    (a, b) => b.proficiencyLevel - a.proficiencyLevel
  )

  const getProficiencyColor = (level: number, isBlockchainSpecific: boolean = false) => {
    if (isBlockchainSpecific) {
      if (level >= 4) return 'bg-secondary'
      if (level >= 3) return 'bg-secondary/80'
      if (level >= 2) return 'bg-secondary/60'
      return 'bg-secondary/40'
    } else {
      if (level >= 4) return 'bg-green-500'
      if (level >= 3) return 'bg-blue-500'
      if (level >= 2) return 'bg-yellow-500'
      return 'bg-gray-400'
    }
  }

  const getProficiencyText = (level: number) => {
    if (level >= 4) return 'Expert'
    if (level >= 3) return 'Advanced'
    if (level >= 2) return 'Intermediate'
    return 'Beginner'
  }

  // Calculate years of experience for each category
  const categoryExperience = Object.entries(categorizedTechnologies).reduce(
    (acc, [category, techs]) => {
      acc[category] = techs.reduce((sum, tech) => sum + tech.yearsOfExperience, 0) / techs.length
      return acc
    },
    {} as Record<string, number>
  )

  return (
    <div className="space-y-8">
      {/* View Mode Toggle */}
      <div className="flex justify-center mb-4">
        <div className="inline-flex rounded-md shadow-sm bg-white dark:bg-gray-800 p-1">
          <button
            onClick={() => setViewMode('grid')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'grid'
                ? 'bg-primary text-white'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            Grid Viewf
          </button>
          <button
            onClick={() => setViewMode('chart')}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'chart'
                ? 'bg-primary text-white'
                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
            }`}
          >
            Chart View
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2 justify-center">
        {categories.map(category => (
          <button
            key={category}
            onClick={() => setSelectedCategory(category)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 ${
              selectedCategory === category
                ? category === 'blockchain'
                  ? 'bg-secondary text-white'
                  : 'bg-primary text-white'
                : 'bg-white dark:bg-gray-800 text-gray-900 dark:text-white hover:bg-blue-100 dark:hover:bg-blue-900'
            }`}
          >
            {category === 'all' ? 'All Skills' : category}
          </button>
        ))}
      </div>

      {viewMode === 'grid' ? (
        /* Skills Grid */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {sortedTechnologies.map((tech, index) => {
            const isBlockchainTech = tech.category === 'blockchain' || tech.isBlockchainSpecific
            return (
              <SlideIn key={tech._id} delay={index * 0.05}>
                <div
                  className={`rounded-lg p-6 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 ${
                    isBlockchainTech
                      ? 'bg-gradient-to-br from-white to-secondary/5 dark:from-gray-800 dark:to-secondary/20 border border-secondary/30'
                      : 'bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {/* Icon and Name */}
                  <div className="flex items-center mb-4">
                    {tech.icon && (
                      <div className="w-12 h-12 mr-4 flex-shrink-0">
                        <Image
                          src={urlFor(tech.icon).width(48).height(48).url()}
                          alt={`${tech.name} icon`}
                          width={48}
                          height={48}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    )}
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {tech.name}
                        </h3>
                        {isBlockchainTech && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-secondary/20 text-secondary dark:text-secondary-light">
                            Blockchain
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{tech.category}</p>
                    </div>
                  </div>

                  {/* Proficiency Level */}
                  <div className="mb-4">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm font-medium text-gray-900 dark:text-white">
                        Proficiency
                      </span>
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {getProficiencyText(tech.proficiencyLevel)}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                      <div
                        className={`h-2 rounded-full transition-all duration-500 ${getProficiencyColor(tech.proficiencyLevel, isBlockchainTech)}`}
                        style={{ width: `${(tech.proficiencyLevel / 5) * 100}%` }}
                      ></div>
                    </div>
                  </div>

                  {/* Years of Experience */}
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Experience:</span>
                    <span className="font-medium text-gray-900 dark:text-white">
                      {tech.yearsOfExperience} {tech.yearsOfExperience === 1 ? 'year' : 'years'}
                    </span>
                  </div>

                  {/* Blockchain Details (if applicable) */}
                  {tech.isBlockchainSpecific && tech.blockchainDetails && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      {tech.blockchainDetails.blockchainType && (
                        <div className="flex justify-between items-center text-sm mb-2">
                          <span className="text-gray-600 dark:text-gray-400">Type:</span>
                          <span className="font-medium text-gray-900 dark:text-white">
                            {tech.blockchainDetails.blockchainType}
                          </span>
                        </div>
                      )}

                      {tech.blockchainDetails.networks &&
                        tech.blockchainDetails.networks.length > 0 && (
                          <div className="mt-2">
                            <span className="text-sm text-gray-600 dark:text-gray-400 block mb-1">
                              Networks:
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {tech.blockchainDetails.networks.map((network, idx) => (
                                <span
                                  key={idx}
                                  className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-secondary/10 text-secondary dark:text-secondary-light"
                                >
                                  {network}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              </SlideIn>
            )
          })}
        </div>
      ) : (
        /* Chart View */
        <FadeIn>
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
              Skills Proficiency Chart
            </h3>
            <div className="space-y-4">
              {sortedTechnologies.map((tech, index) => {
                const isBlockchainTech = tech.category === 'blockchain' || tech.isBlockchainSpecific
                return (
                  <div key={tech._id} className="relative">
                    <div className="flex items-center mb-1">
                      <span className="w-32 text-sm font-medium text-gray-900 dark:text-white truncate">
                        {tech.name}
                      </span>
                      <div className="flex-1 ml-4">
                        <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-4">
                          <div
                            className={`h-4 rounded-full transition-all duration-500 ${getProficiencyColor(tech.proficiencyLevel, isBlockchainTech)}`}
                            style={{ width: `${(tech.proficiencyLevel / 5) * 100}%` }}
                          >
                            <span className="px-2 text-xs text-white font-semibold leading-4">
                              {tech.proficiencyLevel}/5
                            </span>
                          </div>
                        </div>
                      </div>
                      <span className="ml-2 w-16 text-xs text-gray-600 dark:text-gray-400">
                        {tech.yearsOfExperience} {tech.yearsOfExperience === 1 ? 'yr' : 'yrs'}
                      </span>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        </FadeIn>
      )}

      {/* Skills Summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-lg border border-gray-200 dark:border-gray-700">
        <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">Skills Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {Object.entries(categorizedTechnologies).map(([category, techs]) => {
            const isBlockchainCategory = category === 'blockchain'
            const avgProficiency =
              techs.reduce((sum, tech) => sum + tech.proficiencyLevel, 0) / techs.length

            return (
              <div
                key={category}
                className={`text-center p-4 rounded-lg ${
                  isBlockchainCategory
                    ? 'bg-secondary/10 border border-secondary/30'
                    : 'bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-700'
                }`}
              >
                <div
                  className={`text-3xl font-bold ${isBlockchainCategory ? 'text-secondary dark:text-secondary-light' : 'text-primary'}`}
                >
                  {techs.length}
                </div>
                <div className="text-sm font-medium text-gray-900 dark:text-white mb-2">
                  {category.charAt(0).toUpperCase() + category.slice(1)}
                </div>
                <div className="flex items-center justify-center">
                  <div className="text-xs text-gray-600 dark:text-gray-400">
                    Avg. Proficiency: {avgProficiency.toFixed(1)}/5
                  </div>
                </div>
                <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                  Avg. Experience: {categoryExperience[category].toFixed(1)} years
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default SkillsVisualization
